import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import GetImageUrl from './GetImageUrl';

interface Testimonial {
    id: number;
    name: string;
    role: string;
    company: string;
    text: string;
    rating: number;
    image: string;
}

interface TestimonialsProps {
    testimonies: Testimonial[];
}

export default function Testimonials({ testimonies }: TestimonialsProps) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = () => {
        if (scrollRef.current) {
            const { scrollWidth, clientWidth, scrollLeft } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = scrollRef.current.clientWidth / 2;
            scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        checkScroll();

        const handleResize = () => checkScroll();
        window.addEventListener('resize', handleResize);

        const scrollEl = scrollRef.current;
        scrollEl?.addEventListener('scroll', checkScroll);

        return () => {
            window.removeEventListener('resize', handleResize);
            scrollEl?.removeEventListener('scroll', checkScroll);
        };
    }, [testimonies]);

    return (
        <section className="relative pt-0 pb-12 bg-gray-50">
            <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <motion.h2
                        className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
                        initial={{ scale: 0.8, opacity: 0 }}
                        whileInView={{ scale: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                    >
                        What Our Customers Say
                    </motion.h2>
                    <motion.p
                        className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                        initial={{ x: -50, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        Don't just take our word for it. Here's what industry professionals say about our products and service.
                    </motion.p>
                </div>

                {/* Scrollable testimonials */}
                <div className="relative flex items-center">
                    {canScrollLeft && (
                        <button
                            onClick={() => scroll('left')}
                            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-20 bg-slate-800/80 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-black/40 hover:text-gray-900 text-white transition"
                        >
                            <ChevronLeft size={24} />
                        </button>
                    )}

                    <div
                        ref={scrollRef}
                        className="flex space-x-6 overflow-x-auto scroll-smooth py-4 px-2 sm:px-4"
                        style={{ scrollbarWidth: 'none' }}
                    >
                        {testimonies.map((testimonial, idx) => (
                            <motion.div
                                key={testimonial.id}
                                className="flex-shrink-0 w-80 sm:w-96 md:w-80 bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow cursor-pointer"
                                initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="flex items-center mb-4">
                                    <Quote className="text-orange-600 mr-2" size={24} />
                                    <div className="flex items-center">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} size={16} className="text-yellow-400 fill-current" />
                                        ))}
                                    </div>
                                </div>

                                <p className="text-gray-600 leading-relaxed mb-6 italic">
                                    "{testimonial.text}"
                                </p>

                                <div className="flex items-center">
                                    <img
                                        src={GetImageUrl(testimonial.image)}
                                        alt={testimonial.name}
                                        className="w-12 h-12 rounded-full object-cover mr-4"
                                    />
                                    <div>
                                        <div className="font-semibold text-slate-900">{testimonial.name}</div>
                                        <div className="text-sm text-gray-600">{testimonial.role}</div>
                                        <div className="text-sm text-orange-600 font-medium">{testimonial.company}</div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {canScrollRight && (
                        <button
                            onClick={() => scroll('right')}
                            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-20 bg-slate-800/80 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white/40 hover:text-gray-900 text-white transition"
                        >
                            <ChevronRight size={24} />
                        </button>
                    )}

                    {/* Hide scrollbar */}
                    <style>{`
            .scroll-smooth::-webkit-scrollbar { display: none; }
            .scroll-smooth { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>
                </div>
            </div>
        </section>
    );
};
